import { unpack_validation } from 'z3k-parser/validation/types';
import { genIdAndIncCounter } from 'z3k-parser/lib/auxiliary';
export function arrayGetSet(key) {
    return {
        get(config) {
            return config[key];
        },
        set(config, new_value) {
            return { ...config, [key]: new_value };
        },
        validation_result(validation_result) {
            return unpack_validation(validation_result, key);
        },
        validation_warnings_result(validation_result) {
            return unpack_validation(validation_result, key);
        },
    };
}
export function createAccessors(getset, defaultObject) {
    const empty_array = [];
    const getValidationResult = (validation_result) => getset.validation_result(validation_result);
    const getValidationResultById = (config, validation_result, object_id) => {
        const index_of_element = self.all(config).findIndex(({ id }) => id == object_id);
        if (index_of_element === -1) {
            return null;
        }
        return unpack_validation(getset.validation_result(validation_result), index_of_element);
    };
    const self = {
        validationResult(validation_result) {
            return getValidationResult(validation_result);
        },
        validationWarningsResult(validation_result) {
            return getValidationResult(validation_result);
        },
        validationResultById(config, validation_result, object_id) {
            return getValidationResultById(config, validation_result, object_id);
        },
        validationWarningsResultById(config, validation_result, object_id) {
            return getValidationResultById(config, validation_result, object_id);
        },
        all(config) {
            return config != null ? getset.get(config) ?? empty_array : empty_array;
        },
        where(config, filter) {
            return self.all(config).filter(filter);
        },
        except(config, except) {
            return self.where(config, (x, i, arr) => !except(x, i, arr));
        },
        one(config, filter) {
            return self.all(config).find(filter);
        },
        some(config, filter) {
            return self.where(config, filter).length > 0;
        },
        every(config, filter) {
            return self.except(config, filter).length == 0;
        },
        dontExist(config, filter) {
            return !self.some(config, filter);
        },
        byId(config, object_id) {
            return self.one(config, (item) => item.id == object_id);
        },
        idIn(config, object_ids) {
            return self.where(config, (obj) => object_ids.includes(obj.id));
        },
        modify(config, filter, modifier) {
            const old_array = self.all(config);
            let changed = false;
            const new_array = old_array.map((item, index, array) => {
                if (filter(item, index, array)) {
                    const newItem = modifier(item);
                    changed ||= newItem != item;
                    return newItem;
                }
                return item;
            });
            if (!changed)
                return config;
            return getset.set(config, new_array);
        },
        modifyById(config, object_id, modifier) {
            return self.modify(config, (item) => item.id == object_id, modifier);
        },
        applySetterById(config, object_id, setter) {
            return self.modifyById(config, object_id, (item) => ({ ...item, ...setter }));
        },
        replaceById(config, object_id, replacement) {
            return self.modifyById(config, object_id, () => replacement);
        },
        remove(config, filter) {
            const old_array = self.all(config);
            const new_array = old_array.filter((item, index, array) => !filter(item, index, array));
            if (new_array.length == old_array.length)
                return config;
            return getset.set(config, new_array);
        },
        removeById(config, object_id) {
            return self.remove(config, (item) => item.id == object_id);
        },
        add(config, object) {
            if ('id' in object && object.id != null) {
                return [
                    object.id,
                    getset.set(config, [...self.all(config), object]),
                ];
            }
            else {
                const [new_id, new_config] = genIdAndIncCounter(config);
                return [
                    new_id,
                    getset.set(new_config, [
                        ...self.all(config),
                        { ...object, id: new_id },
                    ]),
                ];
            }
        },
        addDefault(config) {
            const [new_id, new_config] = genIdAndIncCounter(config);
            return [
                new_id,
                getset.set(new_config, [
                    ...self.all(config),
                    { ...defaultObject, id: new_id },
                ]),
            ];
        },
        addCopy(config, obj) {
            const item = self.byId(config, obj.id);
            const copy_name = item && `${item.name}` + `(копия)`;
            let [new_id, new_config] = [null, config];
            if (item && 'calibration' in item) {
                [new_id, new_config] = genIdAndIncCounter(config);
                new_config = {
                    ...new_config,
                    calibration_tables: [
                        ...(new_config['calibration_tables'] ?? []),
                        {
                            id: new_id,
                            table: [],
                            multiply_x: 10,
                        },
                    ],
                };
            }
            const new_object_copy = {
                ...item,
                name: copy_name,
                ...(new_id && { calibration: new_id }),
            };
            [new_id, new_config] = genIdAndIncCounter(new_config);
            return [
                new_id,
                getset.set(new_config, [
                    ...self.all(config),
                    { ...new_object_copy, id: new_id },
                ]),
            ];
        },
        /**
         * @function
         * Нужно передать объект с ID
         * Хорошо подходит для master-setting
         * @param config - конфиг
         * @param obj - объект этого типа
         * @return Z3KConfig
         */
        createIfNotExists(config, obj) {
            if (self.byId(config, obj.id)) {
                return config;
            }
            return self.add(config, obj)[1];
        },
        defaultObject,
    };
    return self;
}
